<script>
	// NPM Modules
	import { createEventDispatcher } from 'svelte';
	// Stores
	import SportStore from '@/Stores/Sport/SportStore';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	// Services
	import { notify } from '@/Services/Notify';
	import MultiGroupStore from '@/Stores/Sport/MultiGroupStore';
	import MarketTypeStore from '@/Stores/Sport/MarketTypeStore';
	import TextInput from '@/Components/TextInput.svelte';

	const dispatch = createEventDispatcher();

	export let open;
	export let sport;
	export let multiGroupId = 'create';

	let sending = false;
	let selectedSport = sport.id;
	let errors = null;

	SportStore.getSports() && multiGroupId === 'create' ? MultiGroupStore.newMultiGroup(sport) : '';

	$: selectedSport && selectedSportChanged();
	$: marketTypes = $MarketTypeStore && $MarketTypeStore.sortBy('order').all();
	$: multiGroup = multiGroupId && $MultiGroupStore && $MultiGroupStore.firstWhere('id', multiGroupId);
	$: multiGroup && setMultiGroupName();
	$: overrideFormData = {
		name: undefined
	};

	function setMultiGroupName() {
		if (!overrideFormData.name) {
			overrideFormData.name = multiGroup.name;
		}
	}

	function createMultiGroup() {
		sending = true;

		MultiGroupStore.createMultiGroup(sport.id, multiGroup.preparePayload(overrideFormData))
			.then(() => {
				notify({
					type: 'success',
					title: 'Multi group created',
					message: 'Multi group successfully created!',
					timeout: 3
				});
				open = false;
				dispatch('created');
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: 'Problem creating new multi group',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}

	function updateMultiGroup() {
		sending = true;
		MultiGroupStore.updateMultiGroup(multiGroup, overrideFormData)
			.then(() => {
				notify({
					type: 'success',
					title: 'Multi group updated',
					message: 'Multi group successfully updated!',
					timeout: 3
				});
				open = false;
				dispatch('updated');
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: 'Problem updating multi group',
					timeout: 5
				});
			})
			.finally(() => (sending = false));
	}

	async function selectedSportChanged() {
		await MarketTypeStore.getMarketTypesForSport(selectedSport);
	}

	function changeSport(sport_id) {
		if (typeof parseInt(sport_id) !== 'undefined' && sport_id !== false) {
			selectedSport = parseInt(sport_id);
		} else {
			selectedSport = false;
		}
	}
</script>

{#if open}
	<Modal
		on:close={() => {
			open = false;
			MultiGroupStore.getMultiGroupsForSport(multiGroup.sport.id);
		}}
		className="md:w-2/3 lg:w-2/3 md:mx-auto"
	>
		<h2 slot="header" class="text-xl font-bold text-center">
			{#if multiGroupId == 'create'}
				Create a new multi group for {sport.name}
			{:else}
				Update multi group - {multiGroup.name}
			{/if}
		</h2>
		<div class="flex flex-col w-full px-2 pt-2 text-center">
			<!-- /Page Header -->
			{#if $SportStore && $MultiGroupStore}
				<div class="px-2 pt-2 bg-gray-100 rounded pb-0 w-full mb-2">
					<!-- START MULTI GROUP SECTION -->
					<div class="flex flex-col w-full space-x-2 min-h-104">
						<!-- MULTI GROUP NAME -->
						<div class="flex w-1/3">
							<TextInput
								name="name"
								label="Multi group name"
								className="py-4 w-full"
								placeholder="Enter multi group name"
								errors={errors && errors.name}
								bind:value={overrideFormData.name}
							/>
						</div>
						<div class="flex test w-full space-x-2">
							<!-- MARKET TYPES SELECTED -->
							<div class="w-2/3">
								<div class="flex items-center w-full mb-2 space-x-4">
									<div class="font-semibold">
										Market types in this group ({multiGroup.excluded_market_types.data.count()})
									</div>
									<button
										on:click={() => MultiGroupStore.resetMultiGroup(multiGroupId)}
										class="py-1 -mb-1 text-xs font-normal btn btn-primary-action"
									>
										Clear
									</button>
								</div>
								<div class="w-full p-1 overflow-y-auto bg-gray-200 border">
									{#if multiGroup.excluded_market_types.data.count() === 0}
										<div class="py-4 mx-2 text-center">Add some market types to this group...</div>
									{/if}
									{#each multiGroup.excluded_market_types.data.all() as marketType}
										<div class="flex items-center w-full space-x-2">
											<PillMultiSelect
												action="remove"
												on:remove={() => MultiGroupStore.removeMarketTypeFromGroup(marketType, multiGroupId)}
												bgColour={'bg-green-500'}
												label={`${marketType.name} ${marketType.label ? '-' + marketType.label : ''}  [${
													marketType.sport.name
												}]`}
											/>
										</div>
									{/each}
								</div>
							</div>
							<!-- AVAILABLE MARKET TYPES FROM SELECTED SPORT -->
							<div class="w-1/3 mt-2">
								<div class="flex flex-col items-center justify-start w-full mb-2">
									<!-- SPORT SELECTION -->
									<div class="flex w-full justify-start text-left">
										<SelectInput
											label="Select a sport"
											className="py-2 w-full"
											name="sport"
											required={true}
											bind:value={selectedSport}
											on:change={(event) => changeSport(event.target.value)}
										>
											<option value={false}>Select Sport...</option>
											{#each $SportStore.sortBy('name').all() as sport (sport.id)}
												{#if parseInt(sport.id) === parseInt(selectedSport)}
													<option value={sport.id} selected>{sport.name}</option>
												{:else}
													<option value={sport.id}>{sport.name}</option>
												{/if}
											{/each}
										</SelectInput>
									</div>
									<div class="font-semibold">
										Available Market Types
										{#if selectedSport}
											({`${
												$MarketTypeStore.count() - multiGroup.excluded_market_types.data.count()
											}/${$MarketTypeStore.count()}`})
										{/if}
									</div>
								</div>
								<div class="w-full p-2 overflow-y-auto bg-gray-200 border">
									{#if selectedSport}
										{#each marketTypes as marketType}
											<!-- ONLY SHOW THE MARKET TYPE WITH IF THE MARKET TYPE HAS NOT BEEN ADDED ALREADY -->
											{#if !multiGroup.excluded_market_types.data.pluck('id').all().includes(marketType.id)}
												<div class="w-full">
													<PillMultiSelect
														on:add={() => MultiGroupStore.addMarketTypeToGroup(marketType, multiGroupId)}
														bgColour={'bg-green-500'}
														label={marketType.name}
													/>
												</div>
											{/if}
										{/each}
									{/if}
								</div>
							</div>
						</div>
					</div>
					<!-- END PARENT MARKETS -->
				</div>
			{/if}

			<LoadingButton
				on:click={() => {
					if (multiGroupId == 'create') {
						createMultiGroup();
					} else {
						updateMultiGroup();
					}
				}}
				loading={sending}
				className="mb-4 btn btn-primary"
			>
				{#if multiGroupId == 'create'}
					Create multi group
				{:else}
					Update multi group
				{/if}
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}>Cancel</button>
		</div>
	</Modal>
{/if}
